import React, { useEffect, useCallback, Suspense } from 'react';
import * as ReactRouterDOM from 'react-router-dom';
import CookieConsentBanner from './components/common/CookieConsentBanner';
import OfflineBanner from './components/common/OfflineBanner';
import ProtectedRoute from './components/common/ProtectedRoute';
import ToastContainer from './components/common/ToastContainer';
import { APP_PATHS } from './config/appRoutes';
import { FEATURE_FLAGS } from './config/featureFlags';
import { PERMISSIONS } from './config/permissions';
import { useAuth } from './hooks/useAuth';
import { useInactivityTimeout } from './hooks/useInactivityTimeout';
import useWebSocket from './hooks/useWebSocket';
import { useRealtimeReconciliation } from './hooks/useRealtimeReconciliation';
import ConfigurationService from './services/ConfigurationService';
import PerformanceService from './services/performanceService';
import AuthenticatedShell from './components/common/AuthenticatedShell';
import ModuleErrorBoundary from './components/common/ModuleErrorBoundary';
import { useFeatureFlagStore } from './stores/useFeatureFlagStore';
import { useSystemConfigStore } from './stores/useSystemConfigStore';
import { useToastStore } from './stores/useToastStore';
import { useNotificationStore } from './stores/useNotificationStore';
import { AlertType, StudentNotification } from './types';
import { AdminDashboardSkeleton } from './components/admin/dashboard/DashboardSkeletons';
import { registerPrefetchRoutes } from './utils/routePrefetcher';

// ============================================================================
// LAZY-LOADED PAGE FACTORIES
// Factories are extracted into named constants so the SAME reference is used
// for both React.lazy() AND the prefetch registry — single source of truth.
// ============================================================================

// Public/Auth Pages (not prefetched — accessed before authentication)
const LandingPage = React.lazy(() => import('./pages/landing/LandingPage'));
const ContactPage = React.lazy(() => import('./pages/landing/ContactPage'));
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage'));
const PasswordChangePage = React.lazy(() => import('./pages/auth/PasswordChangePage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/auth/ResetPasswordPage'));
const TwoFactorAuthPage = React.lazy(() => import('./pages/auth/TwoFactorAuthPage'));
const SecureNotePage = React.lazy(() => import('./pages/auth/SecureNotePage'));

// --- Admin Page Factories ---
const adminDashboardFactory = () => import('./pages/admin/AdminDashboardPage');
const userManagementFactory = () => import('./pages/admin/UserManagementPage');
const roleUsersFactory = () => import('./pages/admin/RoleUsersPage');
const createUserFactory = () => import('./pages/admin/CreateUserPage');
const bulkCreateUsersFactory = () => import('./pages/admin/BulkCreateUsersPage');
const scholarshipManagementFactory = () => import('./pages/admin/ScholarshipManagementPage');
const scholarshipApplicationsFactory = () => import('./pages/admin/ScholarshipApplicationsPage');
const scholarshipApplicationDetailsFactory = () => import('./pages/admin/ScholarshipApplicationDetailsPage');
const systemConfigFactory = () => import('./pages/admin/SystemConfigurationPage');
const systemReportsFactory = () => import('./pages/admin/SystemReportsLogsPage');
const analyticsFactory = () => import('./pages/admin/AnalyticsDashboard');
const feedbackManagementFactory = () => import('./pages/admin/FeedbackManagementPage');
const feedbackDetailsFactory = () => import('./pages/admin/FeedbackDetailsPage');
const adminProfileFactory = () => import('./pages/admin/AdminProfileSettingsPage');
const paymentOptionsFactory = () => import('./pages/admin/PaymentOptionsPage');
const viewFeeStructuresFactory = () => import('./pages/admin/ViewFeeStructuresPage');
const viewInstallmentPlansFactory = () => import('./pages/admin/ViewInstallmentPlansPage');
const rolePermissionsFactory = () => import('./pages/admin/RolePermissionsPage');
const roleDetailsFactory = () => import('./pages/admin/RoleDetailsPage');
const navigationSettingsFactory = () => import('./pages/admin/NavigationSettingsPage');
const dynamicConfigFactory = () => import('./pages/admin/DynamicConfigPage');
const workflowDesignerFactory = () => import('./pages/admin/WorkflowDesigner');
const observabilityFactory = () => import('./pages/admin/ObservabilityDashboardPage');
const workflowMonitorFactory = () => import('./pages/admin/WorkflowMonitorPage');
const workflowObservabilityFactory = () => import('./pages/admin/WorkflowObservabilityPage');
const auditLogFactory = () => import('./pages/admin/AuditLogViewerPage');
const forensicAuditFactory = () => import('./pages/admin/ForensicAuditPage');
const jitAccessFactory = () => import('./pages/admin/JitAccessConsolePage');
const communicationRulesFactory = () => import('./pages/admin/CommunicationRulesPage');
const communicationRuleEditorFactory = () => import('./pages/admin/CommunicationRuleEditorPage');
const securityCenterFactory = () => import('./pages/admin/SecurityCenterPage');
const financialInsightsFactory = () => import('./pages/admin/FinancialInsightsPage');
const systemGovernanceFactory = () => import('./pages/admin/SystemGovernancePage');
const notificationManagementFactory = () => import('./pages/admin/NotificationManagementPage');
const deadLetterQueueFactory = () => import('./pages/admin/DeadLetterQueuePage');
const adminNotificationsFactory = () => import('./pages/admin/AdminNotificationsPage');
const eligibilityConfigFactory = () => import('./pages/admin/EligibilityConfigPage');
const adminAcademicSessionFactory = () => import('./pages/admin/AcademicSessionManagementPage');
const adminAcademicRecordEntryFactory = () => import('./pages/admin/AcademicRecordEntryPage');
const adminPromotionPreviewFactory = () => import('./pages/admin/PromotionPreviewPage');
const adminPromotionResultsFactory = () => import('./pages/admin/PromotionResultsPage');

// --- Staff Page Factories ---
const staffDashboardFactory = () => import('./pages/staff/StaffDashboardPage');
const staffPromotionPreviewFactory = () => import('./pages/staff/PromotionPreviewPage');
const staffPromotionResultsFactory = () => import('./pages/staff/PromotionResultsPage');
const paymentVerificationFactory = () => import('./pages/staff/PaymentVerificationPage');
const studentAccountInquiryFactory = () => import('./pages/staff/StudentAccountInquiryPage');
const studentFinancialRecordFactory = () => import('./pages/staff/StudentFinancialRecordPage');
const feeStructureManagementFactory = () => import('./pages/staff/FeeStructureManagementPage');
const feeManagementFactory = () => import('./pages/staff/FeeManagementPage');
const installmentPlanManagementFactory = () => import('./pages/staff/InstallmentPlanManagementPage');
const financialReportsFactory = () => import('./pages/staff/FinancialReportsPage');
const announcementsFactory = () => import('./pages/staff/AnnouncementsPage');
const staffProfileFactory = () => import('./pages/staff/StaffProfilePage');
const assignedFeedbackFactory = () => import('./pages/staff/AssignedFeedbackPage');
const assignedFeedbackDetailsFactory = () => import('./pages/staff/AssignedFeedbackDetailsPage');
const paymentTrendsFactory = () => import('./pages/staff/PaymentTrendsPage');
const staffNotificationsFactory = () => import('./pages/staff/StaffNotificationsPage');
const scholarshipFinanceApprovalFactory = () => import('./pages/staff/ScholarshipFinanceApprovalPage');
const scholarshipFinanceReviewDetailsFactory = () => import('./pages/staff/ScholarshipFinanceReviewDetailsPage');

// --- Student Page Factories ---
const studentDashboardFactory = () => import('./pages/student/StudentDashboardPage');
const makePaymentFactory = () => import('./pages/student/MakePaymentPage');
const paymentHistoryFactory = () => import('./pages/student/PaymentHistoryPage');
const installmentScheduleFactory = () => import('./pages/student/InstallmentSchedulePage');
const downloadReceiptsFactory = () => import('./pages/student/DownloadReceiptsPage');
const studentNotificationsFactory = () => import('./pages/student/NotificationsPage');
const submitFeedbackFactory = () => import('./pages/student/SubmitFeedbackPage');
const studentProfileFactory = () => import('./pages/student/StudentProfilePage');
const paymentStatusFactory = () => import('./pages/student/PaymentStatusPage');
const myScholarshipsFactory = () => import('./pages/student/MyScholarshipsPage');

// General Pages
const searchFactory = () => import('./pages/SearchPage');
const privacyPolicyFactory = () => import('./pages/legal/PrivacyPolicy');
const termsOfServiceFactory = () => import('./pages/legal/TermsOfService');

// ============================================================================
// REACT.LAZY COMPONENTS (Consume the shared factories)
// ============================================================================

// Admin Pages
const AdminDashboardPage = React.lazy(adminDashboardFactory);
const UserManagementPage = React.lazy(userManagementFactory);
const RoleUsersPage = React.lazy(roleUsersFactory);
const CreateUserPage = React.lazy(createUserFactory);
const BulkCreateUsersPage = React.lazy(bulkCreateUsersFactory);
const ScholarshipManagementPage = React.lazy(scholarshipManagementFactory);
const ScholarshipApplicationsPage = React.lazy(scholarshipApplicationsFactory);
const ScholarshipApplicationDetailsPage = React.lazy(scholarshipApplicationDetailsFactory);
const SystemConfigurationPage = React.lazy(systemConfigFactory);
const SystemReportsLogsPage = React.lazy(systemReportsFactory);
const AnalyticsDashboard = React.lazy(analyticsFactory);
const FeedbackManagementPage = React.lazy(feedbackManagementFactory);
const FeedbackDetailsPage = React.lazy(feedbackDetailsFactory);
const AdminProfileSettingsPage = React.lazy(adminProfileFactory);
const PaymentOptionsPage = React.lazy(paymentOptionsFactory);
const ViewFeeStructuresPage = React.lazy(viewFeeStructuresFactory);
const ViewInstallmentPlansPage = React.lazy(viewInstallmentPlansFactory);
const RolePermissionsPage = React.lazy(rolePermissionsFactory);
const RoleDetailsPage = React.lazy(roleDetailsFactory);
const NavigationSettingsPage = React.lazy(navigationSettingsFactory);
const DynamicConfigPage = React.lazy(dynamicConfigFactory);
const WorkflowDesigner = React.lazy(workflowDesignerFactory);
const ObservabilityDashboardPage = React.lazy(observabilityFactory);
const WorkflowMonitorPage = React.lazy(workflowMonitorFactory);
const WorkflowObservabilityPage = React.lazy(workflowObservabilityFactory);
const AuditLogViewerPage = React.lazy(auditLogFactory);
const ForensicAuditPage = React.lazy(forensicAuditFactory);
const JitAccessConsolePage = React.lazy(jitAccessFactory);
const CommunicationRulesPage = React.lazy(communicationRulesFactory);
const CommunicationRuleEditorPage = React.lazy(communicationRuleEditorFactory);
const SecurityCenterPage = React.lazy(securityCenterFactory);
const FinancialInsightsPage = React.lazy(financialInsightsFactory);
const SystemGovernancePage = React.lazy(systemGovernanceFactory);
const NotificationManagementPage = React.lazy(notificationManagementFactory);
const DeadLetterQueuePage = React.lazy(deadLetterQueueFactory);
const AdminNotificationsPage = React.lazy(adminNotificationsFactory);
const EligibilityConfigPage = React.lazy(eligibilityConfigFactory);
const AdminAcademicSessionManagementPage = React.lazy(adminAcademicSessionFactory);
const AdminAcademicRecordEntryPage = React.lazy(adminAcademicRecordEntryFactory);
const AdminPromotionPreviewPage = React.lazy(adminPromotionPreviewFactory);
const AdminPromotionResultsPage = React.lazy(adminPromotionResultsFactory);

// Staff Pages
const StaffPromotionPreviewPage = React.lazy(staffPromotionPreviewFactory);
const StaffPromotionResultsPage = React.lazy(staffPromotionResultsFactory);
const StaffDashboardPage = React.lazy(staffDashboardFactory);
const PaymentVerificationPage = React.lazy(paymentVerificationFactory);
const StudentAccountInquiryPage = React.lazy(studentAccountInquiryFactory);
const StudentFinancialRecordPage = React.lazy(studentFinancialRecordFactory);
const FeeStructureManagementPage = React.lazy(feeStructureManagementFactory);
const FeeManagementPage = React.lazy(feeManagementFactory);
const InstallmentPlanManagementPage = React.lazy(installmentPlanManagementFactory);
const FinancialReportsPage = React.lazy(financialReportsFactory);
const AnnouncementsPage = React.lazy(announcementsFactory);
const StaffProfilePage = React.lazy(staffProfileFactory);
const AssignedFeedbackPage = React.lazy(assignedFeedbackFactory);
const AssignedFeedbackDetailsPage = React.lazy(assignedFeedbackDetailsFactory);
const PaymentTrendsPage = React.lazy(paymentTrendsFactory);
const StaffNotificationsPage = React.lazy(staffNotificationsFactory);
const ScholarshipFinanceApprovalPage = React.lazy(scholarshipFinanceApprovalFactory);
const ScholarshipFinanceReviewDetailsPage = React.lazy(scholarshipFinanceReviewDetailsFactory);

// Student Pages
const StudentDashboardPage = React.lazy(studentDashboardFactory);
const MakePaymentPage = React.lazy(makePaymentFactory);
const PaymentHistoryPage = React.lazy(paymentHistoryFactory);
const InstallmentSchedulePage = React.lazy(installmentScheduleFactory);
const DownloadReceiptsPage = React.lazy(downloadReceiptsFactory);
const NotificationsPage = React.lazy(studentNotificationsFactory);
const SubmitFeedbackPage = React.lazy(submitFeedbackFactory);
const StudentProfilePage = React.lazy(studentProfileFactory);
const PaymentStatusPage = React.lazy(paymentStatusFactory);
const MyScholarshipsPage = React.lazy(myScholarshipsFactory);

// General Pages
const SearchPage = React.lazy(searchFactory);
const PrivacyPolicy = React.lazy(privacyPolicyFactory);
const TermsOfService = React.lazy(termsOfServiceFactory);

// ============================================================================
// DYNAMIC PREFETCH REGISTRATION
// Routes self-register using APP_PATHS constants — zero hardcoded strings.
// Parameterized routes (containing ':') are automatically skipped by the registry.
// ============================================================================
registerPrefetchRoutes([
  // Admin
  [APP_PATHS.ADMIN.DASHBOARD, adminDashboardFactory],
  [APP_PATHS.ADMIN.USER_MANAGEMENT.ROOT, userManagementFactory],
  [APP_PATHS.ADMIN.USER_MANAGEMENT.CREATE, createUserFactory],
  [APP_PATHS.ADMIN.USER_MANAGEMENT.BULK_CREATE, bulkCreateUsersFactory],
  [APP_PATHS.ADMIN.SCHOLARSHIP_MANAGEMENT.ROOT, scholarshipManagementFactory],
  [APP_PATHS.ADMIN.SCHOLARSHIP_MANAGEMENT.APPLICATIONS, scholarshipApplicationsFactory],
  [APP_PATHS.ADMIN.SYSTEM_CONFIGURATION.ROOT, systemConfigFactory],
  [APP_PATHS.ADMIN.SYSTEM_CONFIGURATION.ROLES, rolePermissionsFactory],
  [APP_PATHS.ADMIN.SYSTEM_CONFIGURATION.NAVIGATION, navigationSettingsFactory],
  [APP_PATHS.ADMIN.SYSTEM_CONFIGURATION.DYNAMIC_UI, dynamicConfigFactory],
  [APP_PATHS.ADMIN.SYSTEM_CONFIGURATION.WORKFLOWS, workflowDesignerFactory],
  [APP_PATHS.ADMIN.REPORTS_LOGS, systemReportsFactory],
  [APP_PATHS.ADMIN.ANALYTICS, analyticsFactory],
  [APP_PATHS.ADMIN.FEEDBACK_MANAGEMENT, feedbackManagementFactory],
  [APP_PATHS.ADMIN.PROFILE_SETTINGS, adminProfileFactory],
  [APP_PATHS.ADMIN.PAYMENT_OPTIONS, paymentOptionsFactory],
  [APP_PATHS.ADMIN.FEE_STRUCTURES, viewFeeStructuresFactory],
  [APP_PATHS.ADMIN.INSTALLMENT_PLANS, viewInstallmentPlansFactory],
  [APP_PATHS.ADMIN.OBSERVABILITY.ROOT, observabilityFactory],
  [APP_PATHS.ADMIN.OBSERVABILITY.DLQ, deadLetterQueueFactory],
  [APP_PATHS.ADMIN.WORKFLOWS.ROOT, workflowMonitorFactory],
  [APP_PATHS.ADMIN.WORKFLOWS.FORENSIC_AUDIT, forensicAuditFactory],
  [APP_PATHS.ADMIN.WORKFLOWS.OBSERVABILITY, workflowObservabilityFactory],
  [APP_PATHS.ADMIN.AUDIT_LOGS, auditLogFactory],
  [APP_PATHS.ADMIN.JIT_ACCESS, jitAccessFactory],
  [APP_PATHS.ADMIN.COMMUNICATION_RULES.ROOT, communicationRulesFactory],
  [APP_PATHS.ADMIN.SECURITY_CENTER, securityCenterFactory],
  [APP_PATHS.ADMIN.FINANCIAL_INSIGHTS, financialInsightsFactory],
  [APP_PATHS.ADMIN.SYSTEM_GOVERNANCE, systemGovernanceFactory],
  [APP_PATHS.ADMIN.NOTIFICATION_MANAGEMENT, notificationManagementFactory],
  [APP_PATHS.ADMIN.NOTIFICATIONS, adminNotificationsFactory],
  [APP_PATHS.ADMIN.ELIGIBILITY_CONFIG, eligibilityConfigFactory],
  [APP_PATHS.ADMIN.ACADEMIC_SESSIONS, adminAcademicSessionFactory],
  [APP_PATHS.ADMIN.ACADEMIC_RECORD_ENTRY, adminAcademicRecordEntryFactory],
  // Staff
  [APP_PATHS.STAFF.DASHBOARD, staffDashboardFactory],
  [APP_PATHS.STAFF.PAYMENT_VERIFICATION, paymentVerificationFactory],
  [APP_PATHS.STAFF.STUDENT_ACCOUNT_INQUIRY, studentAccountInquiryFactory],
  [APP_PATHS.STAFF.FEE_STRUCTURE_MANAGEMENT, feeStructureManagementFactory],
  [APP_PATHS.STAFF.FEE_MANAGEMENT, feeManagementFactory],
  [APP_PATHS.STAFF.INSTALLMENT_PLAN_CONFIGURATION, installmentPlanManagementFactory],
  [APP_PATHS.STAFF.FINANCIAL_REPORTS, financialReportsFactory],
  [APP_PATHS.STAFF.ANNOUNCEMENTS, announcementsFactory],
  [APP_PATHS.STAFF.PAYMENT_TRENDS, paymentTrendsFactory],
  [APP_PATHS.STAFF.NOTIFICATIONS, staffNotificationsFactory],
  [APP_PATHS.STAFF.PROFILE, staffProfileFactory],
  [APP_PATHS.STAFF.FEEDBACK.ROOT, assignedFeedbackFactory],
  [APP_PATHS.STAFF.SCHOLARSHIP_APPROVALS.ROOT, scholarshipFinanceApprovalFactory],
  // Student
  [APP_PATHS.STUDENT.DASHBOARD, studentDashboardFactory],
  [APP_PATHS.STUDENT.MAKE_PAYMENT, makePaymentFactory],
  [APP_PATHS.STUDENT.PAYMENT_HISTORY, paymentHistoryFactory],
  [APP_PATHS.STUDENT.PAYMENT_STATUS, paymentStatusFactory],
  [APP_PATHS.STUDENT.INSTALLMENT_SCHEDULE, installmentScheduleFactory],
  [APP_PATHS.STUDENT.DOWNLOAD_RECEIPTS, downloadReceiptsFactory],
  [APP_PATHS.STUDENT.NOTIFICATIONS, studentNotificationsFactory],
  [APP_PATHS.STUDENT.SUBMIT_FEEDBACK, submitFeedbackFactory],
  [APP_PATHS.STUDENT.PROFILE, studentProfileFactory],
  [APP_PATHS.STUDENT.MY_SCHOLARSHIPS, myScholarshipsFactory],
  // General
  [APP_PATHS.SEARCH, searchFactory],
]);

const LoadingFallback = () => (
  <div className="flex justify-center items-center h-screen">
    <p className="text-xl">Loading Page...</p>
  </div>
);

function App() {
  useRealtimeReconciliation(); // Enable global real-time synchronization
  const InactivityManager = useInactivityTimeout();
  const { user, isLoading: isAuthLoading } = useAuth();
  const fetchPublicConfig = useSystemConfigStore(
    useCallback((state) => state.fetchPublicConfig, [])
  );
  const { fetchFlags, isEnabled, isLoading: isFlagsLoading } = useFeatureFlagStore();

  const addToast = useToastStore((state) => state.addToast);
  // Only connect WebSocket when user is authenticated
  const isAuthenticated = !isAuthLoading && user !== null;
  const { on: onWs, off: offWs } = useWebSocket(ConfigurationService.getSocketUrl(), isAuthenticated);
  const location = ReactRouterDOM.useLocation();

  useEffect(() => {
    // Initialize performance monitoring
    PerformanceService.init();
  }, []);

  useEffect(() => {
    // Track route changes
    PerformanceService.mark(`route_change_to_${location.pathname}`);
  }, [location.pathname]);

  useEffect(() => {
    const recaptchaKey = ConfigurationService.getRecaptchaKey();
    if (!recaptchaKey) return;

    const existingScript = document.getElementById('recaptcha-script');
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = 'recaptcha-script';
      script.src = `${ConfigurationService.getRecaptchaApiUrl()}?render=${recaptchaKey}`;
      script.async = true;
      document.body.appendChild(script);

      return () => {
        const scriptToRemove = document.getElementById('recaptcha-script');
        if (scriptToRemove) {
          document.body.removeChild(scriptToRemove);
        }
      };
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchFlags(controller.signal);
    return () => {
      controller.abort();
    };
  }, [fetchFlags]);

  useEffect(() => {
    if (isAuthLoading) return;

    const controller = new AbortController();
    const signal = controller.signal;
    let notificationTimer: NodeJS.Timeout | null = null;

    if (user) {
      onWs(
        ConfigurationService.getWsEventNewNotification(),
        (notification: StudentNotification) => {
          addToast({ type: AlertType.Info, message: `New Notification: ${notification.subject}` });
          // Pipe incoming real-time notifications directly into the store
          // This closes the Observer Pattern loop, ensuring the sidebar badges increment instantaneously
          useNotificationStore.getState().addNotification(notification);
        }
      );
      onWs(
        ConfigurationService.getWsEventPaymentStatusUpdated(),
        (data: { paymentId: string; status: string; statusDisplay: string }) => {
          addToast({
            type: AlertType.Info,
            message: `Payment ${data.paymentId} status updated to ${data.statusDisplay}.`,
          });
        }
      );
      onWs(
        ConfigurationService.getWsEventScholarshipStatusUpdated(),
        (data: { applicationId: string; newStatus: string; oldStatus: string }) => {
          addToast({
            type: AlertType.Info,
            message: `Scholarship application ${data.applicationId} status changed to ${data.newStatus}.`,
          });
        }
      );
      onWs(
        ConfigurationService.getWsEventPaymentQueueUpdated(),
        (data: { paymentId: string; status: string; verifiedBy: string }) => {
          addToast({
            type: AlertType.Info,
            message: `Payment ${data.paymentId} status updated to ${data.status}. Verified by ${data.verifiedBy}.`,
          });
        }
      );

      // ── Staggered, Deferred Notification Store Hydration ──
      // Populate the global notification store after a delay to clear the network critical path
      // during bootstrap. This prevents concurrent request storms.
      notificationTimer = setTimeout(() => {
        useNotificationStore.getState().fetchNotifications(signal);
      }, 1000);
    }

    // Always fetch public config for branding consistency across ALL roles
    fetchPublicConfig(signal);

    return () => {
      controller.abort();
      if (notificationTimer) {
        clearTimeout(notificationTimer);
      }
      offWs(ConfigurationService.getWsEventNewNotification());
      offWs(ConfigurationService.getWsEventPaymentStatusUpdated());
      offWs(ConfigurationService.getWsEventScholarshipStatusUpdated());
      offWs(ConfigurationService.getWsEventPaymentQueueUpdated());
    };
  }, [user, isAuthLoading, fetchPublicConfig, addToast, onWs, offWs]);

  if (isFlagsLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-xl">Loading Configuration...</p>
      </div>
    );
  }

  return (
    <>
      {InactivityManager}

      <ToastContainer />
      <CookieConsentBanner />
      <OfflineBanner />
      <Suspense fallback={<LoadingFallback />}>
        <ReactRouterDOM.Routes>
          <ReactRouterDOM.Route path={APP_PATHS.PUBLIC.LANDING} element={<LandingPage />} />
          <ReactRouterDOM.Route path={APP_PATHS.PUBLIC.CONTACT} element={<ContactPage />} />
          <ReactRouterDOM.Route
            path={APP_PATHS.PUBLIC.LOGIN_ROLE}
            element={ 
              // ErrorBoundary and crashing the entire application shell — preserving
              // user access to all other public routes (landing, contact, legal).
              //
              // Principle: Fault Tolerance — isolate component failures, maintain
              // service availability for unrelated functionality.
              <ModuleErrorBoundary moduleName="Login Portal">
                <LoginPage />
              </ModuleErrorBoundary>
            }
          />
          <ReactRouterDOM.Route
            path={APP_PATHS.PUBLIC.LOGIN}
            element={
              <ReactRouterDOM.Navigate
                to={`/login/${ConfigurationService.getDefaultLoginRole()}`}
                replace
              />
            }
          />
          <ReactRouterDOM.Route
            path={APP_PATHS.PUBLIC.PASSWORD_CHANGE}
            element={<PasswordChangePage />}
          />
          <ReactRouterDOM.Route
            path={APP_PATHS.PUBLIC.FORGOT_PASSWORD}
            element={<ForgotPasswordPage />}
          />
          <ReactRouterDOM.Route
            path={APP_PATHS.PUBLIC.RESET_PASSWORD}
            element={<ResetPasswordPage />}
          />
          <ReactRouterDOM.Route
            path={APP_PATHS.PUBLIC.TWO_FACTOR_AUTH}
            element={<TwoFactorAuthPage />}
          />
          <ReactRouterDOM.Route
            path={APP_PATHS.PUBLIC.SECURE_NOTE}
            element={<SecureNotePage />}
          />

          {/* Legal Routes */}
          <ReactRouterDOM.Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <ReactRouterDOM.Route path="/terms-of-service" element={<TermsOfService />} />

          {/* Admin Routes */}
          {/* Persistent Layout Shell for All Authenticated Routes */}
          <ReactRouterDOM.Route element={<AuthenticatedShell />}>
            {/* Admin Routes */}
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.DASHBOARD}
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.USER.VIEW}
                  loadingFallback={<AdminDashboardSkeleton />}
                >
                  <Suspense fallback={<AdminDashboardSkeleton />}>
                    <AdminDashboardPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* ... other admin routes ... */}
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.USER_MANAGEMENT.BULK_CREATE}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.USER.MANAGE}>
                  <BulkCreateUsersPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.USER_MANAGEMENT.CREATE}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.USER.MANAGE}>
                  <CreateUserPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.USER_MANAGEMENT.ROOT}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.USER.MANAGE}>
                  <UserManagementPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.USER_MANAGEMENT.USERS_BY_ROLE}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.USER.MANAGE}>
                  <RoleUsersPage />
                </ProtectedRoute>
              }
            />

            {isEnabled(FEATURE_FLAGS.SCHOLARSHIPS) && (
              <>
                <ReactRouterDOM.Route
                  path={APP_PATHS.ADMIN.SCHOLARSHIP_MANAGEMENT.NEW_APPLICATION}
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.SCHOLARSHIP.MANAGE}>
                      <ScholarshipApplicationDetailsPage />
                    </ProtectedRoute>
                  }
                />
                <ReactRouterDOM.Route
                  path={APP_PATHS.ADMIN.SCHOLARSHIP_MANAGEMENT.APPLICATION_DETAILS}
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.SCHOLARSHIP.MANAGE}>
                      <ScholarshipApplicationDetailsPage />
                    </ProtectedRoute>
                  }
                />
                <ReactRouterDOM.Route
                  path={APP_PATHS.ADMIN.SCHOLARSHIP_MANAGEMENT.APPLICATIONS}
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.SCHOLARSHIP.MANAGE}>
                      <ScholarshipApplicationsPage />
                    </ProtectedRoute>
                  }
                />
                <ReactRouterDOM.Route
                  path={APP_PATHS.ADMIN.SCHOLARSHIP_MANAGEMENT.ROOT}
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.SCHOLARSHIP.MANAGE}>
                      <ScholarshipManagementPage />
                    </ProtectedRoute>
                  }
                />
              </>
            )}

            {isEnabled(FEATURE_FLAGS.FEEDBACK_SYSTEM) && (
              <>
                <ReactRouterDOM.Route
                  path={APP_PATHS.ADMIN.FEEDBACK_MANAGEMENT}
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.FEEDBACK.MANAGE}>
                      <FeedbackManagementPage />
                    </ProtectedRoute>
                  }
                />
                <ReactRouterDOM.Route
                  path={APP_PATHS.ADMIN.FEEDBACK_DETAILS}
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.FEEDBACK.MANAGE}>
                      <FeedbackDetailsPage />
                    </ProtectedRoute>
                  }
                />
              </>
            )}

            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.SYSTEM_CONFIGURATION.ROLE_DETAILS}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ROLE.MANAGE}>
                  <RoleDetailsPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.SYSTEM_CONFIGURATION.ROLES}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ROLE.MANAGE}>
                  <RolePermissionsPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.SYSTEM_CONFIGURATION.NAVIGATION}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.SYSTEM.CONFIG}>
                  <NavigationSettingsPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.SYSTEM_CONFIGURATION.DYNAMIC_UI}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.SYSTEM.CONFIG}>
                  <DynamicConfigPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.SYSTEM_CONFIGURATION.WORKFLOWS}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.SYSTEM.CONFIG}>
                  <WorkflowDesigner />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.SYSTEM_CONFIGURATION.ROOT}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.SYSTEM.CONFIG}>
                  <SystemConfigurationPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.REPORTS_LOGS}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.REPORTS.GENERATE}>
                  <SystemReportsLogsPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.ANALYTICS}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.REPORTS.GENERATE}>
                  <AnalyticsDashboard />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.OBSERVABILITY.ROOT}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.OBSERVABILITY.VIEW}>
                  <ObservabilityDashboardPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.OBSERVABILITY.DLQ}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.SYSTEM.DLQ_MANAGE}>
                  <DeadLetterQueuePage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.WORKFLOWS.ROOT}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.WORKFLOW.VIEW}>
                  <WorkflowMonitorPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.WORKFLOWS.FORENSIC_AUDIT}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.WORKFLOW.FORENSIC}>
                  <ForensicAuditPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.WORKFLOWS.OBSERVABILITY}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.WORKFLOW.OBSERVABILITY}>
                  <WorkflowObservabilityPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.AUDIT_LOGS}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.AUDIT.VIEW}>
                  <AuditLogViewerPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.JIT_ACCESS}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.JIT.MANAGE}>
                  <JitAccessConsolePage />
                </ProtectedRoute>
              }
            />

            {/* Communication Rules */}
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.COMMUNICATION_RULES.NEW}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.COMMUNICATION.MANAGE}>
                  <CommunicationRuleEditorPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.COMMUNICATION_RULES.EDIT}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.COMMUNICATION.MANAGE}>
                  <CommunicationRuleEditorPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.COMMUNICATION_RULES.ROOT}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.COMMUNICATION.MANAGE}>
                  <CommunicationRulesPage />
                </ProtectedRoute>
              }
            />

            {/* Security Center */}
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.SECURITY_CENTER}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.SECURITY.VIEW}>
                  <SecurityCenterPage />
                </ProtectedRoute>
              }
            />

            {/* Financial Insights */}
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.FINANCIAL_INSIGHTS}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.FINANCIAL.VIEW}>
                  <FinancialInsightsPage />
                </ProtectedRoute>
              }
            />

            {/* System Governance */}
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.SYSTEM_GOVERNANCE}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.GOVERNANCE.VIEW}>
                  <SystemGovernancePage />
                </ProtectedRoute>
              }
            />

            {/* Notification Management */}
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.NOTIFICATION_MANAGEMENT}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.NOTIFICATIONS.MANAGE}>
                  <NotificationManagementPage />
                </ProtectedRoute>
              }
            />

            {/* Admin Notification Inbox */}
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.NOTIFICATIONS}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.USER.VIEW}>
                  <AdminNotificationsPage />
                </ProtectedRoute>
              }
            />

            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.PROFILE_SETTINGS}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.AUTHENTICATED.ACCESS}>
                  <AdminProfileSettingsPage />
                </ProtectedRoute>
              }
            />


            {isEnabled(FEATURE_FLAGS.ONLINE_PAYMENTS) && (
              <ReactRouterDOM.Route
                path={APP_PATHS.ADMIN.PAYMENT_OPTIONS}
                element={
                  <ProtectedRoute requiredPermission={PERMISSIONS.PAYMENT_OPTIONS.MANAGE}>
                    <PaymentOptionsPage />
                  </ProtectedRoute>
                }
              />
            )}

            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.FEE_STRUCTURES}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.FEE_STRUCTURE.READ}>
                  <ViewFeeStructuresPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.INSTALLMENT_PLANS}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.INSTALLMENT_PLAN.READ}>
                  <ViewInstallmentPlansPage />
                </ProtectedRoute>
              }
            />

            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.ELIGIBILITY_CONFIG}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ACADEMIC.POLICY_MANAGE}>
                  <EligibilityConfigPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.ACADEMIC_SESSIONS}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ACADEMIC.SESSION_READ}>
                  <AdminAcademicSessionManagementPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.PROMOTION_PREVIEW}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ACADEMIC.SESSION_READ}>
                  <AdminPromotionPreviewPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.PROMOTION_RESULTS}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ACADEMIC.SESSION_READ}>
                  <AdminPromotionResultsPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.ADMIN.ACADEMIC_RECORD_ENTRY}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ACADEMIC.RECORD_IMPORT}>
                  <AdminAcademicRecordEntryPage />
                </ProtectedRoute>
              }
            />

            {/* Student Routes */}
            <ReactRouterDOM.Route
              path={APP_PATHS.STUDENT.DASHBOARD}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.STUDENT.ACCESS}>
                  <StudentDashboardPage />
                </ProtectedRoute>
              }
            />
            {isEnabled(FEATURE_FLAGS.SCHOLARSHIPS) && (
              <ReactRouterDOM.Route
                path={APP_PATHS.STUDENT.MY_SCHOLARSHIPS}
                element={
                  <ProtectedRoute requiredPermission={PERMISSIONS.STUDENT.ACCESS}>
                    <MyScholarshipsPage />
                  </ProtectedRoute>
                }
              />
            )}
            {isEnabled(FEATURE_FLAGS.ONLINE_PAYMENTS) && (
              <>
                <ReactRouterDOM.Route
                  path={APP_PATHS.STUDENT.MAKE_PAYMENT}
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.STUDENT.ACCESS}>
                      <MakePaymentPage />
                    </ProtectedRoute>
                  }
                />
                <ReactRouterDOM.Route
                  path={APP_PATHS.STUDENT.PAYMENT_STATUS}
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.STUDENT.ACCESS}>
                      <PaymentStatusPage />
                    </ProtectedRoute>
                  }
                />
              </>
            )}
            <ReactRouterDOM.Route
              path={APP_PATHS.STUDENT.PAYMENT_HISTORY}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.STUDENT.ACCESS}>
                  <PaymentHistoryPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.STUDENT.INSTALLMENT_SCHEDULE}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.STUDENT.ACCESS}>
                  <InstallmentSchedulePage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.STUDENT.DOWNLOAD_RECEIPTS}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.STUDENT.ACCESS}>
                  <DownloadReceiptsPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.STUDENT.NOTIFICATIONS}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.STUDENT.ACCESS}>
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />
            {isEnabled(FEATURE_FLAGS.FEEDBACK_SYSTEM) && (
              <ReactRouterDOM.Route
                path={APP_PATHS.STUDENT.SUBMIT_FEEDBACK}
                element={
                  <ProtectedRoute requiredPermission={PERMISSIONS.STUDENT.ACCESS}>
                    <SubmitFeedbackPage />
                  </ProtectedRoute>
                }
              />
            )}
            <ReactRouterDOM.Route
              path={APP_PATHS.STUDENT.PROFILE}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.STUDENT.ACCESS}>
                  <StudentProfilePage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.SEARCH}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.SEARCH.GLOBAL}>
                  <SearchPage />
                </ProtectedRoute>
              }
            />

            {/* Staff Routes */}
            <ReactRouterDOM.Route
              path={APP_PATHS.STAFF.DASHBOARD}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.STAFF.ACCESS}>
                  <StaffDashboardPage />
                </ProtectedRoute>
              }
            />
            {isEnabled(FEATURE_FLAGS.ONLINE_PAYMENTS) && (
              <ReactRouterDOM.Route
                path={APP_PATHS.STAFF.PAYMENT_VERIFICATION}
                element={
                  <ProtectedRoute requiredPermission={PERMISSIONS.PAYMENT.VERIFY}>
                    <PaymentVerificationPage />
                  </ProtectedRoute>
                }
              />
            )}
            {isEnabled(FEATURE_FLAGS.SCHOLARSHIPS) && (
              <>
                <ReactRouterDOM.Route
                  path={APP_PATHS.STAFF.SCHOLARSHIP_APPROVALS.DETAILS}
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.SCHOLARSHIP.APPROVE}>
                      <ScholarshipFinanceReviewDetailsPage />
                    </ProtectedRoute>
                  }
                />
                <ReactRouterDOM.Route
                  path={APP_PATHS.STAFF.SCHOLARSHIP_APPROVALS.ROOT}
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.SCHOLARSHIP.APPROVE}>
                      <ScholarshipFinanceApprovalPage />
                    </ProtectedRoute>
                  }
                />
              </>
            )}
            <ReactRouterDOM.Route
              path={APP_PATHS.STAFF.STUDENT_ACCOUNT_INQUIRY}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.STUDENT_FINANCIALS.ACCESS}>
                  <StudentAccountInquiryPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.STAFF.STUDENT_FINANCIAL_RECORD}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.STUDENT_FINANCIALS.ACCESS}>
                  <StudentFinancialRecordPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.STAFF.FEE_STRUCTURE_MANAGEMENT}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.FEE_STRUCTURE.MANAGE}>
                  <FeeStructureManagementPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.STAFF.FEE_MANAGEMENT}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.FEE_STRUCTURE.MANAGE}>
                  <FeeManagementPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.STAFF.INSTALLMENT_PLAN_CONFIGURATION}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.INSTALLMENT_PLAN.MANAGE}>
                  <InstallmentPlanManagementPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.STAFF.FINANCIAL_REPORTS}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.REPORTS.GENERATE}>
                  <FinancialReportsPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.STAFF.ANNOUNCEMENTS}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ANNOUNCEMENT.MANAGE}>
                  <AnnouncementsPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.STAFF.PAYMENT_TRENDS}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.REPORTS.GENERATE}>
                  <PaymentTrendsPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.STAFF.NOTIFICATIONS}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.STAFF.ACCESS}>
                  <StaffNotificationsPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.STAFF.PROFILE}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.STAFF.ACCESS}>
                  <StaffProfilePage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.STAFF.PROMOTION_PREVIEW}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ACADEMIC.SESSION_READ}>
                  <StaffPromotionPreviewPage />
                </ProtectedRoute>
              }
            />
            <ReactRouterDOM.Route
              path={APP_PATHS.STAFF.PROMOTION_RESULTS}
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ACADEMIC.SESSION_READ}>
                  <StaffPromotionResultsPage />
                </ProtectedRoute>
              }
            />

            {isEnabled(FEATURE_FLAGS.FEEDBACK_SYSTEM) && (
              <>
                <ReactRouterDOM.Route
                  path={APP_PATHS.STAFF.FEEDBACK.DETAILS}
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.STAFF.ACCESS}>
                      <AssignedFeedbackDetailsPage />
                    </ProtectedRoute>
                  }
                />
                <ReactRouterDOM.Route
                  path={APP_PATHS.STAFF.FEEDBACK.ROOT}
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.STAFF.ACCESS}>
                      <AssignedFeedbackPage />
                    </ProtectedRoute>
                  }
                />
              </>
            )}

            <ReactRouterDOM.Route
              path="*"
              element={<ReactRouterDOM.Navigate to={APP_PATHS.PUBLIC.LANDING} replace />}
            />
          </ReactRouterDOM.Route>
        </ReactRouterDOM.Routes>
      </Suspense>
    </>
  );
}

export default App;
